import { Type, type Static } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  StringField,
  TextField,
  EnumField,
  BooleanField,
  RelationField,
  RelationOne,
} from '@ayocore/platform/common/helpers/schema-types';
import accountingCurrencySchemaDefinition from './currency.schema';
import partnerSchemaDefinition from '@ayocore/erp/crm/schemas/partner.schema';

import { name as moduleName } from '../module.json';
/**
 * Юридические лица — наши (is_own = true) и контрагенты (is_own = false).
 *
 * Контрагент может быть опционально связан с CRM-партнёром через partner_id.
 */
export const AccountingLegalEntitySchema = Type.Object({
  ...SystemFields,

  entity_type: EnumField(['company', 'sole_proprietor', 'individual'], {
    title: 'Тип субъекта',
    description: 'Определяет форму регистрации: юрлицо, ИП или физлицо.',
    default: 'company',
  }),

  short_name: StringField({
    title: 'Краткое наименование',
    minLength: 1,
    maxLength: 255,
    description: 'Краткое наименование, используемое в интерфейсе.',
  }),

  full_name: StringField({
    title: 'Полное наименование',
    nullable: true,
    maxLength: 500,
    description: 'Полное юридическое наименование для печатных форм.',
  }),

  inn: StringField({
    title: 'ИНН',
    minLength: 1,
    maxLength: 20,
    description: 'Идентификационный номер налогоплательщика.',
  }),

  kpp: StringField({
    title: 'КПП',
    nullable: true,
    maxLength: 20,
    description: 'Код причины постановки на учёт (только для юрлиц).',
  }),

  ogrn: StringField({
    title: 'ОГРН/ОГРНИП',
    nullable: true,
    maxLength: 20,
    description: 'Регистрационный номер (ОГРН для юрлиц, ОГРНИП для ИП).',
  }),

  okpo: StringField({
    title: 'ОКПО',
    nullable: true,
    maxLength: 20,
    description: 'Код по Общероссийскому классификатору предприятий и организаций.',
  }),

  legal_address: TextField({
    title: 'Юридический адрес',
    nullable: true,
    description: 'Юридический адрес организации.',
  }),

  phone: StringField({
    title: 'Телефон',
    nullable: true,
    maxLength: 50,
    description: 'Контактный телефон организации.',
  }),

  director_name: StringField({
    title: 'ФИО руководителя',
    nullable: true,
    maxLength: 255,
    description: 'Полное имя руководителя — используется в печатных формах.',
  }),

  accountant_name: StringField({
    title: 'ФИО главного бухгалтера',
    nullable: true,
    maxLength: 255,
    description: 'Полное имя главного бухгалтера — используется в печатных формах.',
  }),

  actual_address: TextField({
    title: 'Фактический адрес',
    nullable: true,
    description: 'Фактический адрес размещения офиса/производства.',
  }),

  is_own: BooleanField({
    title: 'Наше юридическое лицо',
    default: false,
    description: 'Флаг, отмечающий внутренние (принадлежащие компании) юридические лица.',
  }),

  default_currency_id: RelationField(accountingCurrencySchemaDefinition, 'ID валюты по умолчанию', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Валюта, используемая по умолчанию в документах этой компании.',
  }),

  is_active: BooleanField({
    title: 'Активно',
    default: true,
    description: 'Неактивные записи скрываются при выборе в документах.',
  }),

  notes: TextField({
    title: 'Примечания',
    nullable: true,
    description: 'Внутренние комментарии и дополнительные сведения.',
  }),

  partner: RelationOne(partnerSchemaDefinition),
  partner_id: RelationField(partnerSchemaDefinition, 'ID CRM-партнёра', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Связанный CRM-партнёр. Заполняется для контрагентов (is_own = false).',
  }),
});

export type AccountingLegalEntity = Static<typeof AccountingLegalEntitySchema>;

export default defineSchema({
  moduleName,
  tableName: 'legal_entities',
  schema: AccountingLegalEntitySchema,
  additionalIndexes: ['short_name', 'inn', 'kpp', 'ogrn', 'is_active', 'is_own', 'partner_id'],
  dependencies: [accountingCurrencySchemaDefinition.tableName, partnerSchemaDefinition.tableName],
});
